
import React, { memo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TeamRecord } from '@/types/teamTypes';

interface LeagueTableDisplayProps {
  teamStandings: TeamRecord[];
  teamName: string;
  pointsDeductions?: { id: number; team: string; points: number; reason: string | null; season: string; league: string }[];
}

// Memoize the component to prevent unnecessary re-renders
export const LeagueTableDisplay: React.FC<LeagueTableDisplayProps> = memo(({
  teamStandings,
  teamName,
  pointsDeductions = []
}) => {
  if (!teamStandings || teamStandings.length === 0) {
    return (
      <div className="border rounded-md p-4 text-center">
        <p className="text-gray-500">No league table data available.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border h-[calc(100%-2rem)] overflow-auto scrollbar-none">
      <Table>
        <TableHeader className="bg-white sticky top-0">
          <TableRow>
            <TableHead className="w-12 py-1.5">Pos</TableHead>
            <TableHead className="py-1.5">Team</TableHead>
            <TableHead className="text-center py-1.5">P</TableHead>
            <TableHead className="text-center py-1.5">W</TableHead>
            <TableHead className="text-center py-1.5">D</TableHead>
            <TableHead className="text-center py-1.5">L</TableHead>
            <TableHead className="text-right py-1.5">Pts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {teamStandings.map((team, index) => (
            <TableRow 
              key={team.team}
              className={team.team === teamName ? "bg-blue-50" : ""}
            >
              <TableCell className="font-medium py-1">{index + 1}</TableCell>
              <TableCell className="py-1">{team.team}</TableCell>
              <TableCell className="text-center py-1">{team.played}</TableCell>
              <TableCell className="text-center py-1">{team.wins}</TableCell>
              <TableCell className="text-center py-1">{team.draws}</TableCell>
              <TableCell className="text-center py-1">{team.losses}</TableCell>
              <TableCell className="text-right py-1">{team.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {pointsDeductions && pointsDeductions.length > 0 && (
        <div className="text-xs text-gray-600 p-2 border-t">
          <p className="font-medium">Points Deductions:</p>
          {pointsDeductions.map((deduction) => (
            <p key={deduction.id} className="text-xs">
              {deduction.team}: -{deduction.points} points {deduction.reason ? `(${deduction.reason})` : ''}
            </p>
          ))}
        </div>
      )}
    </div>
  );
});

// Display name for debugging
LeagueTableDisplay.displayName = 'LeagueTableDisplay';

export default LeagueTableDisplay;
